import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Cell, Tooltip, LabelList, CartesianGrid,
} from "recharts";
import type {
  LeadDistFilters, FunnelStep, StateTransition, CareStatusBucket, CareGroupBucket, RvaDetailRow,
} from "@analytics/shared";
import { ROLE_LABEL } from "@analytics/shared";
import { api, useAsync, useApp } from "../store.js";
import { OK, WARN, CRIT, UNK, ACC, PageHead, card, cardPad, Chip } from "../lib/ui.js";
import { exportElementToPdf, stampedFilename } from "../lib/pdf.js";

// sequential purple ramp (light → dark); funnel steps get progressively darker
const SEQ = ["#d2cefd", "#b5abfc", "#968ae0", "#7d6fc9", "#655aa6", "#51468a", "#413872", "#332c5c"];
// reserved status colors — always paired with a text label (secondary encoding)
const STATUS_COLOR: Record<string, string> = {
  Assigned: OK, "Handled (contact created)": ACC, Revoked: CRIT, Failed: CRIT,
  Distributing: "#6f93d6", Ignored: UNK,
};
const statusColor = (s: string) => STATUS_COLOR[s] ?? UNK;

// care-status group → color (gray / blue / green / red)
const GROUP_COLOR: Record<string, string> = {
  "chua-chuyen-doi": UNK, "trung-tinh": "#6f93d6", "tich-cuc": OK, "tieu-cuc": CRIT,
};
const groupColor = (g: string) => GROUP_COLOR[g] ?? UNK;

const TOOLTIP: React.CSSProperties = { background: "#232532", border: "1px solid #3f424d", borderRadius: "8px", fontSize: "12px", color: "#e9e9ed" };
const fmt = (n: number) => n.toLocaleString("en-US");
const pct = (n: number) => Math.round(n * 100) + "%";
const pct1 = (n: number) => (n * 100).toFixed(n > 0 && n < 0.1 ? 1 : 0) + "%";

export type Opt = { value: string; label: string };

export function LeadDistribution() {
  const { session } = useApp();
  const scoped = session?.scope != null;
  const impersonating = session?.impersonating ?? null;
  const [groupIds, setGroupIds] = useState<string[]>([]);
  const [flowIds, setFlowIds] = useState<string[]>([]);
  const [projects, setProjects] = useState<string[]>([]);
  const [rvas, setRvas] = useState<string[]>([]);
  const [jobIds, setJobIds] = useState<string[]>([]);
  const [from, setFrom] = useState<string>("");
  const [to, setTo] = useState<string>("");
  const [rvaPage, setRvaPage] = useState<number>(0);

  const filters: LeadDistFilters = useMemo(() => {
    const f: LeadDistFilters = {};
    if (groupIds.length) f.groupIds = groupIds.map(Number);
    if (flowIds.length) f.flowIds = flowIds.map(Number);
    if (projects.length) f.projects = projects;
    if (rvas.length) f.rvas = rvas;
    if (jobIds.length) f.jobIds = jobIds;
    if (from) f.from = new Date(from).getTime();
    if (to) f.to = new Date(to + "T23:59:59").getTime();
    return f;
  }, [groupIds, flowIds, projects, rvas, jobIds, from, to]);
  const key = JSON.stringify(filters);

  const opts = useAsync(() => api.leadDistFilters(), []);
  const src = useAsync(() => api.leadDistSource(), [key]);
  const rep = useAsync(() => api.leadDistReport(filters), [key]);
  const rvaDet = useAsync(() => api.leadDistRvaDetail(filters, rvaPage, 20), [key, rvaPage]);
  const life = useAsync(() => api.leadDistLifecycleFunnel(filters), [key]);
  const crm = useAsync(() => api.leadDistCrmFunnel(filters), [key]);
  const trans = useAsync(() => api.leadDistTransitions(filters), [key]);

  const flowOpts: Opt[] = (opts.data?.flows ?? []).map((f) => ({ value: String(f.id), label: `${f.name} (${fmt(f.count)})` }));
  const groupOpts: Opt[] = (opts.data?.groups ?? []).map((g) => ({ value: String(g.id), label: `${g.name} (${fmt(g.count)})` }));
  const projOpts: Opt[] = (opts.data?.projects ?? []).map((p) => ({ value: p.id, label: `${p.name} (${fmt(p.count)})` }));
  const rvaOpts: Opt[] = (opts.data?.rvas ?? []).map((r) => ({ value: r.user, label: `${r.name} (${fmt(r.count)})` }));
  const jobOpts: Opt[] = (opts.data?.jobs ?? []).map((j) => ({ value: j.id, label: `${j.name} (${fmt(j.count)})` }));

  const r = rep.data;
  const reset = () => { setGroupIds([]); setFlowIds([]); setProjects([]); setRvas([]); setJobIds([]); setFrom(""); setTo(""); setRvaPage(0); };
  const anyFilter = Boolean(groupIds.length || flowIds.length || projects.length || rvas.length || jobIds.length || from || to);

  const reportRef = useRef<HTMLDivElement>(null);
  const [exporting, setExporting] = useState(false);
  const exportPdf = async () => {
    if (!reportRef.current || exporting) return;
    setExporting(true);
    try {
      await exportElementToPdf(reportRef.current, { filename: stampedFilename("bao-cao-phan-phoi-lead") });
    } catch (e) {
      console.error("PDF export failed", e);
      alert("Không xuất được PDF. Vui lòng thử lại.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div ref={reportRef}>
      <PageHead
        title="Báo cáo phân phối lead"
        sub="Lead đã phân phối · tương tác · thu hồi · đổi trạng thái — từ kho lead_distribution + trạng thái Agent Success"
        right={
          <>
            {src.data ? (
              src.data.live
                ? <Chip color={OK}>live data</Chip>
                : <Chip color={WARN}>{src.data.note ?? "sample data"}</Chip>
            ) : null}
            <button
              data-export-hide=""
              onClick={exportPdf}
              disabled={exporting}
              className="btn btn-secondary"
              style={{ fontSize: "12px", height: "32px", display: "inline-flex", alignItems: "center", gap: "6px", cursor: exporting ? "default" : "pointer", opacity: exporting ? 0.7 : 1 }}
            >
              <i className={exporting ? "ph ph-spinner" : "ph ph-file-pdf"} /> {exporting ? "Đang xuất…" : "Xuất PDF"}
            </button>
          </>
        }
      />

      {(scoped || impersonating) && (
        <div style={{ ...cardPad, display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px", borderColor: "#423a6a", background: "rgba(150,138,224,.10)", fontSize: "12px", color: "#cfd3e5" }}>
          <i className="ph ph-funnel" style={{ color: "#b5abfc" }} />
          {impersonating
            ? <span>Đang xem với tư cách <strong style={{ color: "#d2cefd" }}>{impersonating.name}</strong>{session ? ` (${ROLE_LABEL[session.role]})` : ""}{scoped ? ` — dữ liệu giới hạn theo ${session?.scope?.rvaCount ?? 0} RVA` : " — thấy toàn bộ dữ liệu"}.</span>
            : <span>Phạm vi của bạn: <strong style={{ color: "#d2cefd" }}>{session?.scope?.rvaCount ?? 0} RVA</strong> — các bộ lọc & số liệu chỉ tính trên phạm vi này.</span>}
        </div>
      )}

      {/* filter bar */}
      <div style={{ ...cardPad, display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "flex-end", marginBottom: "10px" }}>
        <Field label="Từ"><input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={inputStyle} /></Field>
        <Field label="Đến"><input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={inputStyle} /></Field>
        <MultiSelect label="Dự án" values={projects} onChange={setProjects} options={projOpts} placeholder="Tất cả" />
        <MultiSelect label="Luồng phân phối (Chiến dịch)" values={flowIds} onChange={setFlowIds} options={flowOpts} placeholder="Tất cả" />
        <MultiSelect label="Lượt phân phối" values={jobIds} onChange={setJobIds} options={jobOpts} placeholder="Tất cả" />
        <MultiSelect label="Nhóm tiếp nhận (Core team)" values={groupIds} onChange={setGroupIds} options={groupOpts} placeholder="Tất cả" />
        <MultiSelect label="RVA" values={rvas} onChange={setRvas} options={rvaOpts} placeholder="Tất cả" />
        {anyFilter && (
          <button onClick={reset} className="btn btn-secondary" style={{ fontSize: "12px", height: "32px" }}>
            <i className="ph ph-x" /> Xoá lọc
          </button>
        )}
      </div>

      {/* KPI tiles */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: "9px" }}>
        <Kpi label="Lead đã phân phối" value={r ? fmt(r.distributed) : "…"} sub="Theo bộ lọc hiện tại" c="#e9e9ed" />
        <Kpi label="Đã được tương tác" value={r ? fmt(r.interacted) : "…"} sub={r ? `${pct(safeRate(r.interacted, r.distributed))} · ${fmt(r.notInteracted)} chưa tương tác` : ""} c={OK} />
        <Kpi label="Đã đổi trạng thái" value={r ? fmt(r.converted) : "…"} sub={r ? `${pct(safeRate(r.converted, r.distributed))} lead đã phân phối` : ""} c={OK} />
        <Kpi label="Tích cực" value={r ? fmt(r.positive) : "…"} sub={r ? `${pct(safeRate(r.positive, r.distributed))} lead đã phân phối` : ""} c={OK} />
        <Kpi label="Từ chối tư vấn" value={r ? fmt(r.refuseConsult) : "…"} sub={r ? `${pct(safeRate(r.refuseConsult, r.distributed))} lead đã phân phối` : ""} c={CRIT} />
        <Kpi label="Đã thu hồi" value={r ? fmt(r.revoked) : "…"} sub={r ? `${pct(safeRate(r.revoked, r.distributed))} · tổng ${fmt(r.revokeEvents)} lần thu hồi` : ""} c={CRIT} />
      </div>

      {/* Tình trạng lead */}
      <div style={{ ...cardPad, marginTop: "10px" }}>
        <CardTitle title="Tình trạng lead" subtitle="Trạng thái lấy từ Agent Success (sale cập nhật) · Chuyển đổi = đã đổi khỏi “Chưa liên hệ”" />
        <StatusBar groups={r?.groups ?? []} total={r?.distributed ?? 0} />
        <StatusChips statuses={r?.statuses ?? []} total={r?.distributed ?? 0} />
      </div>

      {/* Chi tiết theo RVA */}
      <div style={{ ...cardPad, marginTop: "10px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "8px" }}>
          <CardTitle title="Chi tiết theo RVA" subtitle="Tương tác (có engagement sau khi phân phối) · Thu hồi · Đổi trạng thái theo từng RVA" />
          <Pager page={rvaDet.data?.page ?? 0} pageSize={rvaDet.data?.pageSize ?? 20} total={rvaDet.data?.total ?? 0} onPage={setRvaPage} />
        </div>
        <RvaTable rows={rvaDet.data?.rows ?? []} loading={rvaDet.loading} />
      </div>

      {/* funnels */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: "10px", marginTop: "10px" }}>
        <FunnelCard title="Vòng đời phân phối" subtitle="Received → Assigned → Handled" steps={life.data?.steps ?? []} />
        <FunnelCard title="Giai đoạn CRM (8 giai đoạn)" subtitle="Giai đoạn chăm sóc — p_8_stage" steps={crm.data?.steps ?? []} />
      </div>

      {/* transitions */}
      <div style={{ ...cardPad, marginTop: "10px" }}>
        <CardTitle title="Chuyển trạng thái phân phối" subtitle="Cách lead dịch chuyển giữa các trạng thái — số lượng và % trên mỗi trạng thái nguồn" />
        <TransitionChart transitions={trans.data?.transitions ?? []} />
      </div>
    </div>
  );
}

const safeRate = (n: number, d: number) => (d > 0 ? n / d : 0);

// ── pieces ──

function StatusBar({ groups, total }: { groups: CareGroupBucket[]; total: number }) {
  if (total <= 0) return <div style={{ height: "14px", background: "#161826", borderRadius: "7px", margin: "12px 0" }} />;
  return (
    <div style={{ margin: "14px 0 10px" }}>
      <div style={{ display: "flex", gap: "2px", height: "14px", borderRadius: "7px", overflow: "hidden" }}>
        {groups.filter((g) => g.count > 0).map((g) => (
          <div key={g.key} title={`${g.label}: ${fmt(g.count)} (${pct(g.pct)})`}
            style={{ width: `${g.pct * 100}%`, background: groupColor(g.key) }} />
        ))}
      </div>
      <div style={{ display: "flex", gap: "16px", flexWrap: "wrap", marginTop: "9px" }}>
        {groups.map((g) => (
          <span key={g.key} style={{ fontSize: "11px", color: "#b2b6ca", display: "inline-flex", alignItems: "center", gap: "6px" }}>
            <span style={{ width: "9px", height: "9px", borderRadius: "2px", background: groupColor(g.key), display: "inline-block" }} />
            {g.label} <b style={{ color: "#e9e9ed" }}>{fmt(g.count)}</b> <span style={{ color: "#75798c" }}>{pct(g.pct)}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function StatusChips({ statuses, total }: { statuses: CareStatusBucket[]; total: number }) {
  const chip = (k: string, label: string, count: number, color: string) => (
    <span key={k || "all"}
      style={{
        display: "inline-flex", alignItems: "center", gap: "6px",
        fontSize: "11px", padding: "4px 9px", borderRadius: "7px",
        border: "1px solid #292b31", background: "#161826", color: "#cfd3e5",
      }}>
      {k && <span style={{ width: "8px", height: "8px", borderRadius: "2px", background: color, display: "inline-block" }} />}
      {label} <b style={{ color: "#e9e9ed" }}>{fmt(count)}</b>
      {total > 0 && <span style={{ color: "#75798c" }}>{pct1(total > 0 ? count / total : 0)}</span>}
    </span>
  );
  return (
    <div style={{ display: "flex", gap: "7px", flexWrap: "wrap", marginTop: "6px" }}>
      {chip("", "Tất cả trạng thái", total, UNK)}
      {statuses.map((s) => chip(s.key, s.label, s.count, groupColor(s.group)))}
    </div>
  );
}

function RvaTable({ rows, loading }: { rows: RvaDetailRow[]; loading: boolean }) {
  return (
    <div style={{ overflowX: "auto" }}>
      <table className="table" style={{ fontSize: "12px", width: "100%" }}>
        <thead><tr>
          <th style={{ paddingLeft: 0 }}>RVA</th>
          <th style={{ textAlign: "right" }}>Được phân phối</th>
          <th style={{ textAlign: "right" }}>Khách đã tương tác</th>
          <th style={{ textAlign: "right" }}>Chưa tương tác</th>
          <th style={{ textAlign: "right" }}>Tỷ lệ tương tác</th>
          <th style={{ textAlign: "right" }}>Thu hồi</th>
          <th style={{ textAlign: "right" }}>Đổi trạng thái</th>
        </tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.user}>
              <td style={{ paddingLeft: 0 }}>
                <div style={{ color: "#e9e9ed" }}>{r.name}</div>
                {r.teamLabel && <div style={{ fontSize: "10px", color: "#75798c" }}>{r.teamLabel}</div>}
              </td>
              <td style={{ textAlign: "right", color: "#e9e9ed", fontVariantNumeric: "tabular-nums" }}>{fmt(r.distributed)}</td>
              <td style={{ textAlign: "right", color: OK, fontVariantNumeric: "tabular-nums" }}>{fmt(r.interacted)}</td>
              <td style={{ textAlign: "right", color: "#9397ab", fontVariantNumeric: "tabular-nums" }}>{fmt(r.notInteracted)}</td>
              <td style={{ textAlign: "right", color: "#b2b6ca", fontVariantNumeric: "tabular-nums" }}>{pct(r.interactRate)}</td>
              <td style={{ textAlign: "right", color: r.revoked > 0 ? CRIT : "#9397ab", fontVariantNumeric: "tabular-nums" }}>{fmt(r.revoked)}</td>
              <td style={{ textAlign: "right", color: "#b2b6ca", fontVariantNumeric: "tabular-nums" }}>{fmt(r.statusChanged)}</td>
            </tr>
          ))}
          {loading && <tr><td colSpan={7} style={{ color: UNK, padding: "14px" }}>Đang tải…</td></tr>}
          {!loading && rows.length === 0 && <tr><td colSpan={7} style={{ color: UNK, textAlign: "center", padding: "18px" }}>Không có RVA nào khớp bộ lọc.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function Pager({ page, pageSize, total, onPage }: { page: number; pageSize: number; total: number; onPage: (p: number) => void }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : page * pageSize + 1;
  const to = Math.min(total, (page + 1) * pageSize);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "11px", color: "#9397ab" }}>
      <span>{from}–{to} / {fmt(total)}</span>
      <button className="btn btn-secondary" style={pagerBtn} disabled={page <= 0} onClick={() => onPage(page - 1)}><i className="ph ph-caret-left" /></button>
      <span>{page + 1}/{pages}</span>
      <button className="btn btn-secondary" style={pagerBtn} disabled={page + 1 >= pages} onClick={() => onPage(page + 1)}><i className="ph ph-caret-right" /></button>
    </div>
  );
}
const pagerBtn: React.CSSProperties = { height: "26px", width: "28px", padding: 0, fontSize: "12px" };

function FunnelCard({ title, subtitle, steps, note }: { title: string; subtitle: string; steps: FunnelStep[]; note?: string }) {
  const data = steps.map((s, i) => ({ ...s, fill: SEQ[Math.min(i, SEQ.length - 1)]! }));
  return (
    <div style={cardPad}>
      <CardTitle title={title} subtitle={subtitle} />
      <ResponsiveContainer width="100%" height={Math.max(150, data.length * 40)}>
        <BarChart layout="vertical" data={data} margin={{ left: 4, right: 56, top: 4, bottom: 4 }}>
          <CartesianGrid horizontal={false} stroke="#232532" />
          <XAxis type="number" hide />
          <YAxis type="category" dataKey="label" width={130} tick={{ fill: "#b2b6ca", fontSize: 11 }} axisLine={false} tickLine={false} />
          <Tooltip contentStyle={TOOLTIP} cursor={{ fill: "rgba(150,138,224,.08)" }}
            formatter={(v: number, _n, p) => [`${fmt(v)} · ${pct((p.payload as FunnelStep).pctOfHead)} of head`, "leads"]} />
          <Bar dataKey="count" radius={[0, 4, 4, 0]} barSize={20} isAnimationActive={false}>
            {data.map((d, i) => <Cell key={i} fill={d.fill} />)}
            <LabelList dataKey="count" position="right" formatter={(v: number) => fmt(v)} fill="#cfd3e5" fontSize={11} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      {note && <div style={{ fontSize: "10px", color: WARN, marginTop: "4px" }}><i className="ph ph-info" /> {note}</div>}
    </div>
  );
}

function TransitionChart({ transitions }: { transitions: StateTransition[] }) {
  const data = transitions.map((t) => ({ ...t, name: `${t.from} → ${t.to}`, fill: statusColor(t.to) }));
  if (data.length === 0) return <div style={{ color: UNK, fontSize: "12px", padding: "14px 0" }}>Không có chuyển trạng thái trong khoảng này.</div>;
  return (
    <ResponsiveContainer width="100%" height={Math.max(140, data.length * 40)}>
      <BarChart layout="vertical" data={data} margin={{ left: 4, right: 70, top: 4, bottom: 4 }}>
        <CartesianGrid horizontal={false} stroke="#232532" />
        <XAxis type="number" hide />
        <YAxis type="category" dataKey="name" width={200} tick={{ fill: "#b2b6ca", fontSize: 11 }} axisLine={false} tickLine={false} />
        <Tooltip contentStyle={TOOLTIP} cursor={{ fill: "rgba(150,138,224,.08)" }}
          formatter={(v: number, _n, p) => [`${fmt(v)} · ${pct((p.payload as StateTransition).pct)} of moves from ${(p.payload as StateTransition).from}`, "transitions"]} />
        <Bar dataKey="count" radius={[0, 4, 4, 0]} barSize={20} isAnimationActive={false}>
          {data.map((d, i) => <Cell key={i} fill={d.fill} />)}
          <LabelList dataKey="count" position="right" formatter={(v: number) => fmt(v)} fill="#cfd3e5" fontSize={11} />
          <LabelList dataKey="pct" position="insideRight" formatter={(v: number) => pct(v)} fill="#161826" fontSize={10} />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function Kpi({ label, value, sub, c }: { label: string; value: string; sub: string; c: string }) {
  return (
    <div style={{ ...card, padding: "11px", borderLeft: "2px solid " + c }}>
      <div style={{ fontSize: "10px", letterSpacing: "0.08em", textTransform: "uppercase", color: "#9397ab" }}>{label}</div>
      <div style={{ fontFamily: "var(--font-heading)", fontSize: "23px", letterSpacing: "-0.02em", color: c, fontVariantNumeric: "tabular-nums", marginTop: "3px" }}>{value}</div>
      <div style={{ fontSize: "11px", color: "#75798c", marginTop: "2px" }}>{sub}</div>
    </div>
  );
}

function CardTitle({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div style={{ marginBottom: "4px" }}>
      <div style={{ fontSize: "13px", fontWeight: 500 }}>{title}</div>
      <div style={{ fontSize: "11px", color: "#75798c" }}>{subtitle}</div>
    </div>
  );
}

export function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
      <label style={{ fontSize: "10px", letterSpacing: "0.06em", textTransform: "uppercase", color: "#75798c" }}>{label}</label>
      {children}
    </div>
  );
}

export function MultiSelect({ label, values, onChange, options, placeholder }: { label: string; values: string[]; onChange: (v: string[]) => void; options: Opt[]; placeholder: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const sel = new Set(values);
  const toggle = (v: string) => {
    const next = new Set(sel);
    if (next.has(v)) next.delete(v); else next.add(v);
    // preserve option order in the emitted list
    onChange(options.filter((o) => next.has(o.value)).map((o) => o.value));
  };
  const summary =
    values.length === 0 ? placeholder
    : values.length === 1 ? (options.find((o) => o.value === values[0])?.label ?? "1 đã chọn")
    : `${values.length} đã chọn`;

  return (
    <Field label={label}>
      <div ref={ref} style={{ position: "relative", minWidth: "170px", maxWidth: "230px" }}>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          style={{ ...inputStyle, width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "6px", cursor: "pointer", textAlign: "left" }}
        >
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: values.length ? "#e9e9ed" : "#75798c" }}>{summary}</span>
          <i className="ph ph-caret-down" style={{ fontSize: "12px", color: "#75798c", flexShrink: 0 }} />
        </button>
        {open && (
          <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, zIndex: 20, minWidth: "100%", maxWidth: "320px", maxHeight: "280px", overflowY: "auto", background: "#1b1d2a", border: "1px solid #292b31", borderRadius: "8px", boxShadow: "0 8px 24px rgba(0,0,0,.4)", padding: "4px" }}>
            {values.length > 0 && (
              <button type="button" onClick={() => onChange([])} style={{ ...menuItem, color: "#9397ab" }}>
                <i className="ph ph-x" style={{ fontSize: "12px" }} /> Bỏ chọn tất cả
              </button>
            )}
            {options.length === 0 && <div style={{ padding: "8px 10px", fontSize: "12px", color: UNK }}>Không có lựa chọn</div>}
            {options.map((o) => {
              const on = sel.has(o.value);
              return (
                <button key={o.value} type="button" onClick={() => toggle(o.value)} style={{ ...menuItem, color: on ? "#e9e9ed" : "#cfd3e5", background: on ? "#232640" : "transparent" }}>
                  <span style={{ width: "14px", height: "14px", borderRadius: "4px", border: "1px solid " + (on ? ACC : "#3f424d"), background: on ? ACC : "transparent", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    {on && <i className="ph ph-check" style={{ fontSize: "10px", color: "#161826" }} />}
                  </span>
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{o.label}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </Field>
  );
}

const menuItem: React.CSSProperties = {
  display: "flex", alignItems: "center", gap: "8px", width: "100%",
  padding: "6px 8px", fontSize: "12px", borderRadius: "6px", border: "none",
  background: "transparent", cursor: "pointer", textAlign: "left",
};

export const inputStyle: React.CSSProperties = {
  height: "32px", background: "#161826", border: "1px solid #292b31", borderRadius: "8px",
  color: "#e9e9ed", fontSize: "12px", padding: "0 8px",
};
